import React, { useState } from 'react';
import PropTypes from 'prop-types';
import Checkbox from 'b2b-ui-components-lib/lib/components/checkbox';

import ErrorMessage from 'b2b-ui-components-lib/lib/components/errorMessage';
import OutsideAlerter from 'b2b-ui-components-lib/lib/components/outsideAlerter';
import Label from 'b2b-ui-components-lib/lib/components/label';

import './multiSelect.scss';

/**
 * @description Custom Multi Select Component
 * @param props
 * @returns a custom multi select component with event handlers
 */
function MultiSelect(props) {
  // attributes
  const { name, label, value, options, required, placeholder, touched, getKey, displayBriefOption, displayExtendedOption, renderSearch } = props;
  // error props
  const { errors, showAllErrors, errorClass, invalidFieldClass } = props;
  // event handlers
  const { onChange, onBlur } = props;
  // classes
  const { multiSelectContainerClass, multiSelectClass, optionContainerClass, optionClass, selectedOptionClass } = props;

  const [isOpen, setIsOpen] = useState(false);

  const isAnySelected = () => value && value.length > 0;

  const isOptionSelected = (option) => value && value.some((selectedValue) => getKey(selectedValue) === getKey(option));

  const handleClickOnSelect = () => {
    setIsOpen((isOpen) => !isOpen);
    onBlur();
  };

  const handleClickOnOption = (e, clickedOption) => {
    let newSelectedOptions = [...value];
    if (newSelectedOptions.some((option) => getKey(option) === getKey(clickedOption))) {
      //delete that selected option from options
      newSelectedOptions = newSelectedOptions.filter((option) => getKey(option) !== getKey(clickedOption));
    } else {
      //add that selected option in options
      newSelectedOptions.push(clickedOption);
    }
    onChange(newSelectedOptions);
    e.stopPropagation();
  };

  const getSelectedOption = () => {
    if (isAnySelected()) {
      return (
        <ul className="selectedOption">
          {value.map((selectedOption, index) => (
            <li className="chips" key={index}>
              {displayBriefOption(selectedOption)}
            </li>
          ))}
        </ul>
      );
    } else {
      return <span className="placeholder">{placeholder}</span>;
    }
  };

  const renderOption = (option, index) => {
    return (
      <li key={index} onClick={(e) => handleClickOnOption(e, option)} className={`${optionClass} ${isOptionSelected(option) ? selectedOptionClass : ''}`}>
        <Checkbox checked={isOptionSelected(option)}>
          <span className="checkboxContent">{displayExtendedOption(option)}</span>
        </Checkbox>
      </li>
    );
  };

  const NoRecord = () => {
    return (
      <li key="no-record" className={`${optionClass} no-record-text`} onClick={(e) => e.stopPropagation()}>
        No Record
      </li>
    );
  };

  const getOptions = (options = []) => {
    if (options.length === 0) {
      return NoRecord();
    }
    return options.map(renderOption);
  };

  const renderSelect = (invalidFieldClass) => {
    return (
      <OutsideAlerter onOutsideClick={() => setIsOpen(false)}>
        <div className="multi_select_outside_controller">
          <div onClick={handleClickOnSelect} className={`${multiSelectContainerClass} ${invalidFieldClass}`}>
            <React.Fragment>
              <div key={name} className={`${multiSelectClass} ${invalidFieldClass}`}>
                {getSelectedOption()}
              </div>
              {isOpen && (
                <div className="option-search-container">
                  {renderSearch()}
                  <div className="option_scroller">
                    <ul className={optionContainerClass}>{options && getOptions(options)}</ul>
                  </div>
                </div>
              )}
            </React.Fragment>
            <Label labelName={required ? label : `${label} (optional)`} labelClass={`label ${isAnySelected() ? 'labelUp' : ''} `} />
          </div>
        </div>
      </OutsideAlerter>
    );
  };

  return (
    <div className={'multiSelectWrapper'}>
      <ErrorMessage {...{ errors, showAllErrors, errorClass, invalidFieldClass }} isErrorVisible={touched} renderChildren={renderSelect} />
    </div>
  );
}

MultiSelect.defaultProps = {
  required: false,
  placeholder: 'Select an option',
  onBlur: () => {},
  multiSelectClass: 'multiSelect',
  multiSelectContainerClass: 'multiSelectContainer',
  optionClass: 'option',
  selectedOptionClass: 'multi_select_option_selected',
  optionContainerClass: 'optionList',
  invalidFieldClass: 'error',
  errorClass: 'multiSelectErrorContainer',
  displayBriefOption: (option) => option.displayName,
  displayExtendedOption: (option) => option.displayName,
  getKey: (option) => option.key,
  renderSearch: () => {}
};

MultiSelect.propTypes = {
  name: PropTypes.string,
  label: PropTypes.string,
  value: PropTypes.array,
  options: PropTypes.array,
  required: PropTypes.bool,
  placeholder: PropTypes.string,
  touched: PropTypes.bool,
  onChange: PropTypes.func,
  onBlur: PropTypes.func,
  getKey: PropTypes.func,
  displayBriefOption: PropTypes.func,
  displayExtendedOption: PropTypes.func,
  multiSelectClass: PropTypes.string,
  multiSelectContainerClass: PropTypes.string,
  optionClass: PropTypes.string,
  selectedOptionClass: PropTypes.string,
  optionContainerClass: PropTypes.string,
  errors: PropTypes.array,
  showAllErrors: PropTypes.bool,
  errorClass: PropTypes.string,
  invalidFieldClass: PropTypes.string,
  renderSearch: PropTypes.func
};

export default MultiSelect;
